import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getRouteConfigByPathname, generateStructuredData } from '../../config/routes.js';

interface PageMetadataProps {
  customTitle?: string;
  customDescription?: string;
  customImage?: string;
}

const PageMetadata: React.FC<PageMetadataProps> = ({ 
  customTitle, 
  customDescription, 
  customImage 
}) => {
  const location = useLocation();
  const routeConfig = getRouteConfigByPathname(location.pathname);

  // Use custom props if provided, otherwise fall back to route config
  const title = customTitle || routeConfig?.title || 'Weaver Games';
  const description = customDescription || routeConfig?.desc || 'Daily word games from Weaver Games';
  const image = customImage || routeConfig?.image || '/images/og/weaver-games-og.png';
  
  // Normalize URL to ensure trailing slash for consistency
  const baseUrl = routeConfig?.url || 'https://wordwormdormdork.com';
  const normalizedUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
  
  const ogTitle = customTitle || routeConfig?.ogTitle || title;
  const ogDesc = customDescription || routeConfig?.ogDesc || description;
  const twitterTitle = customTitle || routeConfig?.twitterTitle || title;
  const twitterDesc = customDescription || routeConfig?.twitterDesc || description;
  const appleTouchIcon = routeConfig?.appleTouchIcon || '/images/icons/weaver-icon.png';

  // Generate JSON-LD structured data based on route
  const structuredData = generateStructuredData(routeConfig);

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={normalizedUrl} />

      {/* Open Graph meta tags */}
      <meta property="og:url" content={normalizedUrl} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDesc} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content="website" />

      {/* Twitter meta tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDesc} />
      <meta name="twitter:image" content={image} />

      {/* Apple touch icon */}
      <link rel="apple-touch-icon" href={appleTouchIcon} />

      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default PageMetadata; 